import { useEffect, useRef } from 'react';
import { PanelRightClose, PanelRightOpen, ShieldCheck } from 'lucide-react';
import UserProfilePanel from './UserProfilePanel';
import OrdersList from './OrdersList';
import HotProductPanel from './HotProductPanel';
import { DETAIL_TABS } from '../../lib/chatLabels';

const PANEL_WIDTH = 300;
const RAIL_WIDTH = 52;

function NoSelection() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center">
      <span className="grid h-11 w-11 place-items-center rounded-2xl bg-surface-3 text-ink-3">
        <ShieldCheck size={19} />
      </span>
      <p className="text-[12.5px] font-medium text-ink">No conversation selected</p>
      <p className="text-[11.5px] leading-relaxed text-ink-3">
        Pick a conversation to see participant details, related orders and safety tools.
      </p>
    </div>
  );
}

function SafetyNote() {
  return (
    <div className="rounded-2xl border border-line bg-surface-3/60 p-3.5">
      <p className="flex items-start gap-2 text-[11.5px] leading-relaxed text-ink-2">
        <ShieldCheck size={14} className="mt-px shrink-0 text-accent" />
        Keep every payment and delivery inside Z2Market. Off-platform deals are not covered by
        escrow or buyer protection.
      </p>
    </div>
  );
}

const PANEL_ID = 'z2m-details-tabpanel';
const tabId = (id) => `z2m-details-tab-${id}`;

/**
 * My Orders / User Profile switch. It is the FIRST element of the panel body,
 * directly above the user card, at every width - it used to sit in the chat
 * header from `lg` up and here below `lg`, which meant two copies of the same
 * control in two different places.
 *
 * Real tab semantics: `role="tablist"` / `role="tab"` / `aria-selected`, each
 * tab wired to the shared `role="tabpanel"` body, plus the roving tabindex and
 * Arrow/Home/End keys a tablist is expected to answer (activation follows focus,
 * which is the recommended pattern for a two-tab switch).
 */
function DetailTabs({ value, onChange }) {
  const buttonRefs = useRef([]);

  const onKeyDown = (event) => {
    const current = DETAIL_TABS.findIndex((tab) => tab.id === value);
    let next = null;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      next = (current + 1) % DETAIL_TABS.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      next = (current - 1 + DETAIL_TABS.length) % DETAIL_TABS.length;
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = DETAIL_TABS.length - 1;
    }

    if (next === null) return;

    event.preventDefault();
    onChange(DETAIL_TABS[next].id);
    buttonRefs.current[next]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label="Panel view"
      onKeyDown={onKeyDown}
      // `grid-cols-2` rather than flex-1 buttons: the two labels are different
      // lengths, so equal columns are what keeps "My Orders" and "User Profile"
      // from shifting as you switch. The hairline border is what makes the
      // `sunken` track legible on the panel's `surface-2` in dark mode, where
      // both tokens are the same value.
      className="grid grid-cols-2 gap-1 rounded-xl border border-line bg-sunken p-1"
    >
      {DETAIL_TABS.map((tab, index) => {
        const isActive = value === tab.id;

        return (
          <button
            key={tab.id}
            id={tabId(tab.id)}
            ref={(node) => {
              buttonRefs.current[index] = node;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={PANEL_ID}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.id)}
            className={`min-w-0 truncate rounded-lg px-2 py-2 text-[12.5px] font-medium transition ${
              isActive ? 'bg-surface text-accent shadow-soft' : 'text-ink-2 hover:text-ink'
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Right column.
 *
 * Collapse is a real width transition, not a swap between two layouts: the
 * column animates between 300px and a 52px rail, and because the chat column is
 * `flex-1` it reflows smoothly into the freed space on the same frames.
 *
 * Both states stay mounted (cross-faded) so the transition has something to
 * animate. The hidden one is `inert`, which removes it from the tab order and
 * the accessibility tree - `opacity-0` alone would leave controls focusable.
 *
 * Focusing: collapsing from a control inside the panel would otherwise drop
 * focus to <body> when that control goes inert, so focus is moved to whichever
 * control is now visible.
 */
export default function RightPanel({
  conversation,
  collapsed,
  onToggleCollapsed,
  detailTab,
  onDetailTabChange = () => {},
  panel,
  viewerRole,
  viewerId,
}) {
  /**
   * Hot Product is the COUNTERPARTY's listings. That only makes sense when the
   * viewer is a buyer looking at a seller - a seller chatting with a buyer would
   * be shown the buyer's (non-existent) listings. So it is buyer-only.
   * For sellers the panel is just the order history / profile of the buyer.
   */
  const showHotProducts = viewerRole !== 'SELLER';

  const railButtonRef = useRef(null);
  const panelButtonRef = useRef(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (collapsed) railButtonRef.current?.focus();
    else panelButtonRef.current?.focus();
  }, [collapsed]);

  return (
    <aside
      // Inline width keeps the desktop column exact (and is what the render
      // check reads); max-w caps it on small phones where the slide-over would
      // otherwise be wider than the screen.
      style={{ width: collapsed ? RAIL_WIDTH : PANEL_WIDTH }}
      className="relative h-full max-w-[86vw] shrink-0 overflow-hidden border-l border-line bg-surface-2 transition-[width] duration-300 ease-out lg:max-w-none"
    >
      {/* ── Expanded panel ─────────────────────────────────────────────── */}
      <div
        inert={collapsed}
        className={`absolute top-[env(safe-area-inset-top)] bottom-0 left-0 flex w-full flex-col transition-opacity duration-200 ${
          collapsed ? 'pointer-events-none opacity-0' : 'opacity-100'
        }`}
      >
        <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2.5 lg:px-4">
          {/* The title is kept at every width: with the tab switch now living in
              the body below, a phone would otherwise get a header holding nothing
              but the close control. */}
          <span className="truncate text-[13px] font-semibold text-ink">Details</span>

          <button
            ref={panelButtonRef}
            type="button"
            onClick={onToggleCollapsed}
            aria-expanded="true"
            aria-label="Collapse details panel"
            title="Collapse details panel"
            className="z2-glow-hover grid h-9 w-9 shrink-0 place-items-center rounded-xl text-ink-2 transition hover:bg-surface-3 hover:text-accent"
          >
            <PanelRightClose size={17} />
          </button>
        </header>

        <div className="z2-scroll min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          {conversation ? (
            <>
              <DetailTabs value={detailTab} onChange={onDetailTabChange} />

              <div
                id={PANEL_ID}
                role="tabpanel"
                aria-labelledby={tabId(detailTab)}
                tabIndex={-1}
                className="space-y-3"
              >
                {detailTab === 'profile' ? (
                  <UserProfilePanel
                    conversation={conversation}
                    profile={panel?.profile}
                    orders={panel?.orders ?? []}
                    ordersLoading={panel?.ordersStatus === 'loading'}
                  />
                ) : (
                  <OrdersList
                    orders={panel?.orders ?? []}
                    status={panel?.ordersStatus ?? 'idle'}
                    participantName={conversation.name}
                    // Which side of each order the viewer is on: the row links to
                    // the buyer's order page or the seller's orders page, and the
                    // order's own party ids are the authority here.
                    viewerId={viewerId}
                    viewerRole={viewerRole}
                  />
                )}
              </div>

              {showHotProducts && (
                <HotProductPanel
                  products={panel?.products ?? []}
                  status={panel?.productsStatus ?? 'idle'}
                  collapsed={collapsed}
                  onToggleCollapsed={onToggleCollapsed}
                />
              )}

              <SafetyNote />
            </>
          ) : (
            <NoSelection />
          )}
        </div>
      </div>

      {/* ── Collapsed rail ─────────────────────────────────────────────── */}
      <div
        inert={!collapsed}
        className={`absolute top-[env(safe-area-inset-top)] bottom-0 left-0 flex w-[52px] flex-col items-center gap-3 py-4 transition-opacity duration-200 ${
          collapsed ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        <button
          ref={railButtonRef}
          type="button"
          onClick={onToggleCollapsed}
          aria-expanded="false"
          aria-label="Expand details panel"
          title="Expand details panel"
          className="z2-glow-hover grid h-9 w-9 place-items-center rounded-xl text-ink-2 transition hover:bg-surface-3 hover:text-accent"
        >
          <PanelRightOpen size={17} />
        </button>

        <span className="text-[10px] font-semibold tracking-[0.18em] text-ink-3 [writing-mode:vertical-rl]">
          DETAILS
        </span>
      </div>
    </aside>
  );
}
